import { useEffect, useState } from "react";
import DashboardLayout from "@/components/layout/DashboardLayout";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import api from "@/lib/api";
import { Building2, Users, FileText, Wrench, DollarSign, Loader2 } from "lucide-react";

export default function AdminDashboard() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/admin/dashboard")
      .then((r) => setData(r.data))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  if (loading) {
    return (
      <DashboardLayout title="Admin Dashboard">
        <div className="flex justify-center py-20"><Loader2 className="h-8 w-8 animate-spin text-primary" /></div>
      </DashboardLayout>
    );
  }

  const rooms = data?.rooms || {};
  const occupancyRate = rooms.total > 0 ? Math.round(((rooms.occupied + rooms.reserved) / rooms.total) * 100) : 0;

  const kpis = [
    { label: "Total Rooms", value: rooms.total, sub: `${rooms.available} available`, icon: Building2, color: "text-[#0A2540]" },
    { label: "Occupancy", value: `${occupancyRate}%`, sub: `${rooms.occupied} occupied, ${rooms.reserved} reserved`, icon: Users, color: "text-blue-600" },
    { label: "Pending Apps", value: data?.applications?.pending || 0, sub: "Awaiting review", icon: FileText, color: "text-amber-600" },
    { label: "Active Leases", value: data?.leases?.active || 0, sub: `${data?.leases?.pending_signature || 0} pending signature`, icon: FileText, color: "text-indigo-600" },
    { label: "Pending Docs", value: data?.documents?.pending_review || 0, sub: "Awaiting review", icon: FileText, color: "text-orange-600" },
    { label: "Open Tickets", value: data?.maintenance?.open || 0, sub: "Maintenance requests", icon: Wrench, color: "text-red-600" },
    { label: "Revenue", value: `$${(data?.payments?.total_revenue || 0).toLocaleString()}`, sub: `${data?.payments?.pending || 0} pending`, icon: DollarSign, color: "text-emerald-600" },
  ];

  const statusColors = {
    submitted: "bg-blue-50 text-blue-700",
    under_review: "bg-amber-50 text-amber-700",
    approved: "bg-emerald-50 text-emerald-700",
    open: "bg-red-50 text-red-700",
    assigned: "bg-blue-50 text-blue-700",
    in_progress: "bg-amber-50 text-amber-700",
  };

  return (
    <DashboardLayout title="Admin Dashboard">
      <div className="space-y-8" data-testid="admin-dashboard">
        {/* KPI Cards */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 xl:grid-cols-7 gap-4">
          {kpis.map((kpi) => (
            <Card key={kpi.label} className="border-border/60" data-testid={`kpi-${kpi.label.toLowerCase().replace(/\s/g, "-")}`}>
              <CardContent className="pt-6">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">{kpi.label}</span>
                  <kpi.icon className={`h-4 w-4 ${kpi.color}`} />
                </div>
                <div className="font-heading text-2xl font-bold text-[#0A2540]">{kpi.value}</div>
                <p className="text-xs text-muted-foreground mt-1">{kpi.sub}</p>
              </CardContent>
            </Card>
          ))}
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Recent Applications */}
          <Card className="border-border/60">
            <CardHeader><CardTitle className="font-heading text-lg">Recent Applications</CardTitle></CardHeader>
            <CardContent className="p-0">
              {(data?.recent_applications || []).length === 0 ? (
                <p className="text-center py-8 text-muted-foreground text-sm">No applications yet</p>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Applicant</TableHead>
                      <TableHead>Room</TableHead>
                      <TableHead>Status</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {data.recent_applications.map((a) => (
                      <TableRow key={a._id}>
                        <TableCell className="font-medium">{a.user_name}</TableCell>
                        <TableCell>{a.room_number}</TableCell>
                        <TableCell><Badge className={statusColors[a.status]}>{a.status?.replace(/_/g, " ")}</Badge></TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>

          {/* Recent Maintenance */}
          <Card className="border-border/60">
            <CardHeader><CardTitle className="font-heading text-lg">Recent Maintenance</CardTitle></CardHeader>
            <CardContent className="p-0">
              {(data?.recent_maintenance || []).length === 0 ? (
                <p className="text-center py-8 text-muted-foreground text-sm">No maintenance requests yet</p>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Tenant</TableHead>
                      <TableHead>Category</TableHead>
                      <TableHead>Status</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {data.recent_maintenance.map((m) => (
                      <TableRow key={m._id}>
                        <TableCell className="font-medium">{m.user_name}</TableCell>
                        <TableCell>{m.category}</TableCell>
                        <TableCell><Badge className={statusColors[m.status]}>{m.status?.replace(/_/g, " ")}</Badge></TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        </div>
      </div>
    </DashboardLayout>
  );
}
